import {
  OrgRole,
  isOrgRoleSuperset,
  orgRoleHasEditPermissions,
} from "@hex/common";
import React, { useCallback } from "react";
import { useHistory } from "react-router";

import { useCurrentUser } from "../../../hooks/me/useCurrentUser.js";
import { ORG_ID } from "../../../orgs";
import { Routes } from "../../../route/routes.js";
import { useDialog } from "../../../util/dialogs.js";
import { trackCounterEvent } from "../../../util/trackEvent";
import { useEnforceProjectLimit } from "../../../util/useEnforceProjectLimit.js";
import { useThreadsEnabledForUser } from "../../agent/ask-from-home/hooks/useThreadsEnabledForUser";
import { useFeatureGates } from "../../feature-gate/FeatureGateContext.js";
import { useCreateNewDefaultComponent } from "../../upload/useCreateNewDefaultComponent.js";
import { useCreateNewDefaultExplore } from "../../upload/useCreateNewDefaultExplore.js";
import { useCreateNewDefaultProject } from "../../upload/useCreateNewDefaultProject.js";

export type CreateMenuLocation = "home_sidebar" | "popover_sidebar" | null;

function isNewTabFromClickEvent(event: React.MouseEvent<HTMLElement>): boolean {
  return event.metaKey || event.ctrlKey;
}

export const useCreateThreadButton = ({
  location,
}: {
  location: CreateMenuLocation;
}) => {
  const history = useHistory();
  const { canCreateNewThread } = useThreadsEnabledForUser();
  const createNewThread = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (location) {
        trackCounterEvent(`${location}.thread.create`);
      }

      if (isNewTabFromClickEvent(evt)) {
        window.open(Routes.THREAD.href(ORG_ID, true, {}), "_blank");
      } else {
        history.push(Routes.THREAD.getUrl({}));
      }
    },
    [history, location],
  );

  return {
    createNewThread,
    canCreateNewThread,
  };
};

export const useCanEditProjectsAndCollections = (): boolean => {
  const currentUser = useCurrentUser();
  const orgRole = currentUser?.orgRole;
  const canEditProjectsAndCollections =
    orgRole !== OrgRole.GUEST && orgRoleHasEditPermissions(orgRole);
  return canEditProjectsAndCollections;
};

export const useCreateProjectButton = ({
  location,
}: {
  location: CreateMenuLocation;
}) => {
  const { createPythonProject, createRProject } = useCreateNewDefaultProject();
  const { projectLimitEnforced } = useEnforceProjectLimit();
  const canEditProjectsAndCollections = useCanEditProjectsAndCollections();
  const { openDialog: openImportDialog } = useDialog("project-import");

  const newProjectDisabled =
    !canEditProjectsAndCollections || projectLimitEnforced;
  const createNewProject = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (location) {
        trackCounterEvent(`${location}.projects.python.create`);
      }
      void createPythonProject(undefined, isNewTabFromClickEvent(evt));
    },
    [createPythonProject, location],
  );

  const createNewRProject = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (location) {
        trackCounterEvent(`${location}.projects.r.create`);
      }
      void createRProject(undefined, isNewTabFromClickEvent(evt));
    },
    [createRProject, location],
  );

  const openImportProjectDialog = useCallback(() => {
    if (location) {
      trackCounterEvent(`${location}.projects.upload`);
    }
    openImportDialog();
  }, [location, openImportDialog]);

  return {
    createNewProject,
    createRProject: createNewRProject,
    openImportDialog: openImportProjectDialog,
    newProjectDisabled,
  };
};

export const useCreateComponentButton = ({
  location,
}: {
  location: CreateMenuLocation;
}) => {
  const canEditProjectsAndCollections = useCanEditProjectsAndCollections();

  const { components: componentsEnabled } = useFeatureGates();
  const { openDialog: openComponentImportDialogCallback } =
    useDialog("component-import");

  const {
    createPythonComponent: createPythonComponentCallback,
    createRComponent: createRComponentCallback,
  } = useCreateNewDefaultComponent();

  const newComponentDisabled =
    !canEditProjectsAndCollections || !componentsEnabled;

  const createPythonComponent = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (location) {
        trackCounterEvent(`${location}.components.python.create`);
      }
      void createPythonComponentCallback(isNewTabFromClickEvent(evt));
    },
    [createPythonComponentCallback, location],
  );
  const createRComponent = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (location) {
        trackCounterEvent(`${location}.components.r.create`);
      }
      void createRComponentCallback(isNewTabFromClickEvent(evt));
    },
    [createRComponentCallback, location],
  );
  const openComponentImportDialog = useCallback(() => {
    if (location) {
      trackCounterEvent(`${location}.components.upload`);
    }
    openComponentImportDialogCallback();
  }, [location, openComponentImportDialogCallback]);

  return {
    componentsFeatureGate: componentsEnabled,
    createPythonComponent,
    createRComponent,
    openComponentImportDialog,
    newComponentDisabled,
  };
};

export const useCreateCollectionButton = ({
  location,
}: {
  location: CreateMenuLocation;
}) => {
  const { collections: collectionsFeatureGate } = useFeatureGates();

  const { openDialog: openNewCollectionDialogCallback } =
    useDialog("new-collection");

  const canEditProjectsAndCollections = useCanEditProjectsAndCollections();

  const newCollectionDisabled =
    !collectionsFeatureGate || !canEditProjectsAndCollections;

  const openNewCollectionDialog = useCallback(() => {
    if (location) {
      trackCounterEvent(`${location}.collection.create`);
    }
    openNewCollectionDialogCallback();
  }, [location, openNewCollectionDialogCallback]);

  return {
    openNewCollectionDialog,
    newCollectionDisabled,
    collectionsFeatureGate,
  };
};

export const useCreateExplorationButton = ({
  location,
}: {
  location: CreateMenuLocation;
}) => {
  const { exploreFromScratch: exploreFromScratchFeatureGate } =
    useFeatureGates();
  const currentUser = useCurrentUser();
  const orgRole = currentUser?.orgRole;

  const canEditExplorations =
    orgRole && isOrgRoleSuperset(orgRole, OrgRole.EXPLORER);
  const newExplorationDisabled =
    !canEditExplorations || !exploreFromScratchFeatureGate;

  const createNewExploreCallback = useCreateNewDefaultExplore();
  const createNewExplore = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (location) {
        trackCounterEvent(`${location}.explore.create`);
      }
      void createNewExploreCallback(isNewTabFromClickEvent(evt));
    },
    [location, createNewExploreCallback],
  );

  return {
    createNewExplore,
    newExplorationDisabled,
    canEditExplorations,
    exploreFromScratchFeatureGate,
  };
};
